import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Modal from '@/components/ui/Modal';
import { faCloud, faSpinner } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { handleAPIError, showSuccessToast, showErrorToast } from '@/utils/toasts';

interface SendTestSCNMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTestMessageSent: () => void;
}

export default function SendTestSCNMessageModal({ isOpen, onClose, onTestMessageSent }: SendTestSCNMessageModalProps) {
  const [formData, setFormData] = useState({
    uid: '',
    token: '',
    channel: 'svenbolz-sys',
    title: '',
    content: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.uid.trim()) {
      showErrorToast('Bitte geben Sie eine UID ein');
      return;
    }

    if (!formData.token.trim()) {
      showErrorToast('Bitte geben Sie einen Token ein');
      return;
    }

    if (!formData.channel.trim()) {
      showErrorToast('Bitte geben Sie einen Channel ein');
      return;
    }

    if (!formData.title.trim()) {
      showErrorToast('Bitte geben Sie einen Titel ein');
      return;
    }

    setLoading(true);

    try {
      await APIRoutes.sendSCNMessage({
        uid: formData.uid.trim(),
        token: formData.token.trim(),
        channel: formData.channel.trim(),
        title: formData.title.trim(),
        content: formData.content.trim(),
      });
      showSuccessToast('Test-SCN-Nachricht wurde erfolgreich versendet');
      onTestMessageSent();
      onClose();

      // Reset form
      setFormData({
        uid: '',
        token: '',
        channel: 'svenbolz-sys',
        title: '',
        content: '',
      });
    } catch (err) {
      handleAPIError(err, 'Fehler beim Senden der Test-SCN-Nachricht');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const isFormValid = formData.uid.trim() && formData.token.trim() && formData.channel.trim() && formData.title.trim();

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Test-SCN-Nachricht senden"
      icon={<FontAwesomeIcon icon={faCloud} className="w-5 h-5 text-blue-600" />}
      closeDisabled={loading}
      maxWidth="lg"
    >
      <form onSubmit={handleSubmit} className="p-6">
        <div className="space-y-4">
          <div>
            <label htmlFor="uid" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              UID (Empfänger) *
            </label>
            <input
              type="text"
              id="uid"
              name="uid"
              value={formData.uid}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
              placeholder="user123"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label htmlFor="token" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Token *
            </label>
            <input
              type="text"
              id="token"
              name="token"
              value={formData.token}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
              placeholder="SCN Token"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label htmlFor="channel" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Channel *
            </label>
            <input
              type="text"
              id="channel"
              name="channel"
              value={formData.channel}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="channel-name"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Titel *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Benachrichtigungstitel"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Nachrichteninhalt
            </label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              rows={4}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-vertical"
              placeholder="Test-Nachricht von BolzAI"
              disabled={loading}
            />
          </div>


          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Die SCN-Nachricht wird in die Warteschlange eingereiht und automatisch versendet.
              Sie können den Status der Nachricht in der Liste verfolgen.
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading || !isFormValid}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 cursor-pointer"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                <span>Wird gesendet...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faCloud} className="w-4 h-4" />
                <span>SCN-Nachricht senden</span>
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}
